import {DOCUMENT} from '@angular/common';
import {Inject, Injectable, NgZone, OnDestroy} from '@angular/core';
import {MOBILE_QUERY} from 'app/common/mobile/mobile-view.directives';

/**
 * Long-press on a touch screen opens the same right-click menu a mouse user gets.
 *
 * The app has thirteen right-click menus - Run Details' legs, the runs list, the order sheet, the traffic
 * sheet and its resources bar, the subcontracting lists, and the DHTMLX timelines (run planner, resource
 * scheduler, shift planner, leg planner, tour planner). Every one of them listens for a `contextmenu` event.
 * Android Chrome fires that on a long press; iOS Safari never does, and a phone-sized browser window with
 * touch emulation behaves like iOS. So on those, every one of those menus was unreachable.
 *
 * Rather than a per-screen button for each, a press held still for {@link HOLD_MS} dispatches a real
 * `contextmenu` MouseEvent at the finger's position on the element under it. The screens' own handlers -
 * `(contextmenu)` bindings and DHTMLX's onContextMenu - run unchanged and position their menus at that
 * point, exactly as they do for a right click.
 *
 * Only below 600px and only for touch. The click that follows a long press that opened a menu is swallowed, so
 * lifting the finger does not also open the row; a long press nothing handled stays an ordinary tap. If the platform fires its own contextmenu first (Android), the
 * synthetic one is skipped.
 */
@Injectable({providedIn: 'root'})
export class MobileLongPressService implements OnDestroy {
    private static readonly HOLD_MS = 500;
    private static readonly MOVE_TOLERANCE_PX = 10;

    private _timer: any = null;
    private _startX = 0;
    private _startY = 0;
    private _target: EventTarget | null = null;
    private _fired = false;
    private _nativeFired = false;
    private readonly _media?: MediaQueryList;

    constructor(
        @Inject(DOCUMENT) private readonly _document: Document,
        private readonly _zone: NgZone
    ) {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }
        this._media = window.matchMedia(MOBILE_QUERY);
        // Outside Angular: a timer per touch must not trigger change detection. The dispatched contextmenu
        // re-enters the zone through the screens' own (contextmenu) listeners.
        this._zone.runOutsideAngular(() => {
            this._document.addEventListener('touchstart', this._onStart, {passive: true, capture: true});
            this._document.addEventListener('touchmove', this._onMove, {passive: true, capture: true});
            this._document.addEventListener('touchend', this._onEnd, {capture: true});
            this._document.addEventListener('touchcancel', this._cancel, {capture: true});
            this._document.addEventListener('contextmenu', this._onNativeContextMenu, {capture: true});
            this._document.addEventListener('click', this._onClick, {capture: true});
        });
    }

    /** Called once from AppComponent so the service is constructed at startup. */
    init(): void {}

    ngOnDestroy(): void {
        this._document.removeEventListener('touchstart', this._onStart, {capture: true});
        this._document.removeEventListener('touchmove', this._onMove, {capture: true});
        this._document.removeEventListener('touchend', this._onEnd, {capture: true});
        this._document.removeEventListener('touchcancel', this._cancel, {capture: true});
        this._document.removeEventListener('contextmenu', this._onNativeContextMenu, {capture: true});
        this._document.removeEventListener('click', this._onClick, {capture: true});
        this._cancel();
    }

    private _onStart = (event: TouchEvent): void => {
        if (!this._media?.matches || event.touches.length !== 1) {
            this._cancel();
            return;
        }
        const touch = event.touches[0];
        this._startX = touch.clientX;
        this._startY = touch.clientY;
        this._target = event.target;
        this._fired = false;
        this._nativeFired = false;
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this._fire(), MobileLongPressService.HOLD_MS);
    };

    private _onMove = (event: TouchEvent): void => {
        if (this._timer === null) {
            return;
        }
        const touch = event.touches[0];
        const moved = Math.hypot(touch.clientX - this._startX, touch.clientY - this._startY);
        if (moved > MobileLongPressService.MOVE_TOLERANCE_PX) {
            this._cancel(); // a scroll, not a press
        }
    };

    private _onEnd = (event: TouchEvent): void => {
        if (this._fired) {
            // The finger lifting after a long press is not a tap on whatever is under it.
            event.preventDefault();
        }
        clearTimeout(this._timer);
        this._timer = null;
    };

    private _cancel = (): void => {
        clearTimeout(this._timer);
        this._timer = null;
    };

    private _onNativeContextMenu = (event: MouseEvent): void => {
        // Android fires its own contextmenu on a long press; do not open the menu twice.
        if (event.isTrusted && this._timer !== null) {
            this._nativeFired = true;
            this._cancel();
        }
    };

    private _onClick = (event: MouseEvent): void => {
        if (this._fired) {
            event.preventDefault();
            event.stopPropagation();
            this._fired = false;
        }
    };

    private _fire(): void {
        this._timer = null;
        const target = this._target as Element | null;
        if (!target || this._nativeFired || !target.isConnected) {
            return;
        }
        const contextMenu = new MouseEvent('contextmenu', {
            bubbles: true,
            cancelable: true,
            composed: true,
            view: window,
            button: 2,
            buttons: 2,
            clientX: this._startX,
            clientY: this._startY,
            screenX: this._startX,
            screenY: this._startY
        });
        // Only a press that opened a menu swallows the tap that follows. Every right-click menu in the app cancels
        // the event (ng-zorro's NzContextMenuService.create and the planners' phone handlers call preventDefault).
        // A press nothing handled - a slow tap on a sidebar entry, a button, a link - is still a tap: swallowing it
        // made the menu drawer ignore any tap held past HOLD_MS, so Planning > Orders looked like it would not open.
        this._fired = !target.dispatchEvent(contextMenu);
    }
}
